import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Card, Form, Button, Row, Col, DatePicker, Spin, Table, Switch } from 'antd'
import { FileExcelOutlined, FilePdfOutlined, CaretDownFilled, CaretRightFilled } from '@ant-design/icons'
import BaseListing from 'src/BaseListings'
import { wtGetFromLocalStorage } from 'src/helpers/commons/LocalStorage'
import WidgetSelector from 'src/widgets/Selectors/WidgetSelector'
import { VOUCHER_STATUS_DROPDOWN_OPTIONS } from 'src/models/vouchers'
import WidgetProjectSelector from 'src/widgets/Selectors/WidgetProjectSelector'
import { wtApiCall, wtApiPostWithContentTypeHandling } from 'src/helpers/commons/ApiWrapper'
import WidgetBranchSelector from 'src/widgets/Selectors/WidgetBranchSelector'
import { lzNotifyErrorMessageFromResult } from 'src/helpers/commons/notifications'
import { wtFormatMoney } from 'src/helpers/commons/utils'
import Pdf from '../TrialBalance/Pdf'

const TrailBalanceV2 = () => {
  const base = useMemo(() => new BaseListing('trial_balance_v2'), [])
  const [isLoading, setIsLoading] = useState(false)
  const [form] = Form.useForm()

  const [projects, setProjects] = useState([])
  const [searchMore, setSearchMore] = useState(false)

  const [hideClosingEntries, setHideClosingEntries] = useState(false)
  const [hideZeroBalances, setHideZeroBalances] = useState(true)
  const [showLastLevelOnly, setShowLastLevelOnly] = useState(false)

  const [projectId, setProjectId] = useState()
  const [projectName, setProjectName] = useState('')

  const [voucherStatus, setVoucherStatus] = useState()
  const [voucherStatusName, setVoucherStatusName] = useState('')

  const [branchId, setBranchId] = useState(0)
  const [branchName, setBranchName] = useState('')
  const [dateRange, setDateRange] = useState([])

  const [reportData, setReportData] = useState([])
  const [boldRows, setBoldRows] = useState([])
  const [expandedRows, setExpandedRows] = useState([])
  const [lastLoadedAt, setLastLoadedAt] = useState('')

  const companyName = wtGetFromLocalStorage('company_name')
  const branches = wtGetFromLocalStorage('branches')

  const resetData = useCallback(() => {
    setReportData([])
    setExpandedRows([])
    setBoldRows([])
  }, [])

  const loadProjects = useCallback(() => {
    base.dropdownData('projects', {}, [setProjects], 1)
  }, [base])

  const getLink = useCallback(
    (record, text, key) => {
      if (dateRange.length < 2) {
        return wtFormatMoney(text, '', false, 2)
      }

      if (record.key === 'total') {
        return wtFormatMoney(text, '', false, 2)
      }

      let fromDate = ''
      let toDate = ''

      if (key === 'opening') {
        fromDate = '2000-01-01'
        toDate = dateRange[0].clone().subtract(1, 'day').format('YYYY-MM-DD')
      } else {
        fromDate = dateRange[0].format('YYYY-MM-DD')
        toDate = dateRange[1].format('YYYY-MM-DD')
      }

      const queryParams =
        'fromDate=' +
        fromDate +
        '&toDate=' +
        toDate +
        '&accountId=' +
        +record.id +
        '&voucherStatus=' +
        +voucherStatus +
        '&branchId=' +
        +branchId +
        '&branchName=' +
        branchName +
        '&projectId=' +
        +projectId +
        '&hideClosingEntries=' +
        +hideClosingEntries

      return (
        <a
          href={'/reports?' + queryParams + '#code:account-statement'}
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: 'none', color: '#000000' }}
        >
          {wtFormatMoney(text, '', false, 2)}
        </a>
      )
    },
    [branchId, branchName, dateRange, hideClosingEntries, projectId, voucherStatus],
  )

  const handleExpand = useCallback((record) => {
    if (!record.hasItems) {
      return
    }

    setExpandedRows((prevExpandedRows) => {
      const isExpanded = prevExpandedRows.includes(record.key)
      if (isExpanded) {
        setBoldRows((prevBoldRows) => prevBoldRows.filter((rowKey) => rowKey !== record.key))
        return prevExpandedRows.filter((rowKey) => rowKey !== record.key)
      }

      setBoldRows((prevBoldRows) => [...prevBoldRows, record.key])
      return [...prevExpandedRows, record.key]
    })
  }, [])

  const expandIcon = useCallback(({ expanded, onExpand, record }) => {
    if (!record.hasItems) {
      return <span style={{ marginRight: '3%' }} />
    }

    return (
      <span style={{ marginRight: '2%' }}>
        {expanded ? (
          <CaretDownFilled
            style={{ color: '#706f71', fontSize: 12 }}
            onClick={(event) => onExpand(record, event)}
          />
        ) : (
          <CaretRightFilled
            style={{ color: '#706f71', fontSize: 12 }}
            onClick={(event) => onExpand(record, event)}
          />
        )}
      </span>
    )
  }, [])

  const columns = useMemo(
    () => [
      {
        title: <span style={{ fontSize: '0.8rem', marginLeft: '5%' }}>Account Description</span>,
        dataIndex: 'name',
        key: 'name',
        align: 'left',
        width: 600,
        onCell: (record) => ({
          onClick: () => handleExpand(record),
          style: { cursor: record.hasItems ? 'pointer' : 'default' },
        }),
        render: (text, record) => (
          <span
            style={{
              fontWeight:
                boldRows.includes(record.key) || record.key === 'total' ? 'bold' : 'normal',
              fontSize: '0.8rem',
              cursor: record.hasItems ? 'pointer' : 'default',
            }}
          >
            {text}
          </span>
        ),
      },
      {
        title: <span style={{ fontSize: '0.8rem' }}>Opening Balance</span>,
        children: [
          {
            title: <span style={{ fontSize: '0.8rem' }}>Debit ( .Rs. )</span>,
            dataIndex: 'opening_balance_debit',
            key: 'opening_balance_debit',
            align: 'right',
            render: (text, record) => (
              <span
                style={{
                  fontWeight:
                    boldRows.includes(record.key) || record.key === 'total' ? 'bold' : 'normal',
                  fontSize: '0.8rem',
                }}
              >
                {getLink(record, text, 'opening')}
              </span>
            ),
          },
          {
            title: <span style={{ fontSize: '0.8rem' }}>Credit ( .Rs. )</span>,
            dataIndex: 'opening_balance_credit',
            key: 'opening_balance_credit',
            align: 'right',
            render: (text, record) => (
              <span
                style={{
                  fontWeight:
                    boldRows.includes(record.key) || record.key === 'total' ? 'bold' : 'normal',
                  fontSize: '0.8rem',
                }}
              >
                {getLink(record, text, 'opening')}
              </span>
            ),
          },
        ],
      },
      {
        title: <span style={{ fontSize: '0.8rem' }}>Current Period</span>,
        children: [
          {
            title: <span style={{ fontSize: '0.8rem' }}>Debit ( .Rs. )</span>,
            dataIndex: 'current_period_debit',
            key: 'current_period_debit',
            align: 'right',
            render: (text, record) => (
              <span
                style={{
                  fontWeight:
                    boldRows.includes(record.key) || record.key === 'total' ? 'bold' : 'normal',
                  fontSize: '0.8rem',
                }}
              >
                {getLink(record, text, 'current')}
              </span>
            ),
          },
          {
            title: <span style={{ fontSize: '0.8rem' }}>Credit ( .Rs. )</span>,
            dataIndex: 'current_period_credit',
            key: 'current_period_credit',
            align: 'right',
            render: (text, record) => (
              <span
                style={{
                  fontWeight:
                    boldRows.includes(record.key) || record.key === 'total' ? 'bold' : 'normal',
                  fontSize: '0.8rem',
                }}
              >
                {getLink(record, text, 'current')}
              </span>
            ),
          },
        ],
      },
      {
        title: <span style={{ fontSize: '0.8rem' }}>Closing Balance</span>,
        children: [
          {
            title: <span style={{ fontSize: '0.8rem' }}>Debit ( .Rs. )</span>,
            dataIndex: 'closing_balance_debit',
            key: 'closing_balance_debit',
            align: 'right',
            render: (text, record) => (
              <span
                style={{
                  fontWeight:
                    boldRows.includes(record.key) || record.key === 'total' ? 'bold' : 'normal',
                  fontSize: '0.8rem',
                }}
              >
                {getLink(record, text, 'closing')}
              </span>
            ),
          },
          {
            title: <span style={{ fontSize: '0.8rem' }}>Credit ( .Rs. )</span>,
            dataIndex: 'closing_balance_credit',
            key: 'closing_balance_credit',
            align: 'right',
            render: (text, record) => (
              <span
                style={{
                  fontWeight:
                    boldRows.includes(record.key) || record.key === 'total' ? 'bold' : 'normal',
                  fontSize: '0.8rem',
                }}
              >
                {getLink(record, text, 'closing')}
              </span>
            ),
          },
        ],
      },
    ],
    [boldRows, getLink, handleExpand],
  )

  const changeValues = useCallback(
    (key, value) => {
      resetData()

      if (key === 'branch_id') {
        setBranchId(value.value)
        setBranchName(value.label)
      }
      if (key === 'project_id') {
        setProjectId(value)
        if (value === '') {
          setProjectName('')
        }
      }
      if (key === 'voucher_status') {
        setVoucherStatus(value.value)
        setVoucherStatusName(value.label)
      }
      if (key === 'date_range') {
        setDateRange(value || [])
      }
      if (key === 'show_last_level_only') {
        setShowLastLevelOnly(value)
      }
      if (key === 'hide_zero_balances') {
        setHideZeroBalances(value)
      }
      if (key === 'hide_closing_entries') {
        setHideClosingEntries(value)
      }
    },
    [resetData],
  )

  useEffect(() => {
    if (searchMore && projects.length === 0) {
      loadProjects()
    }
  }, [loadProjects, projects.length, searchMore])

  const getReportData = useCallback(() => {
    if (dateRange.length < 2) {
      lzNotifyErrorMessageFromResult('Please select date range')
      return
    }

    setIsLoading(true)
    wtApiCall(
      'reports/trial_balance_v2',
      {
        branch_id: branchId,
        from_date: dateRange[0].format('YYYY-MM-DD'),
        to_date: dateRange[1].format('YYYY-MM-DD'),
        hide_zero_balances: hideZeroBalances,
        show_last_level_only: showLastLevelOnly,
        project_id: projectId,
        voucher_status: voucherStatus,
        hide_closing_entries: hideClosingEntries,
      },
      'get',
      (response) => {
        const rows = response?.data ?? []
        setReportData(rows)
        setLastLoadedAt(new Date().toLocaleTimeString())
        setIsLoading(false)
      },
      (error) => {
        lzNotifyErrorMessageFromResult(error)
        setIsLoading(false)
      },
    )
  }, [
    branchId,
    dateRange,
    hideClosingEntries,
    hideZeroBalances,
    projectId,
    showLastLevelOnly,
    voucherStatus,
  ])

  const exportToExcel = useCallback(async () => {
    if (reportData.length === 0) {
      lzNotifyErrorMessageFromResult('No data found to export')
      return
    }

    setIsLoading(true)
    try {
      await wtApiPostWithContentTypeHandling('reports/trial_balance_v2/export', {
        data: reportData,
        from_date: dateRange[0]?.format('YYYY-MM-DD'),
        to_date: dateRange[1]?.format('YYYY-MM-DD'),
      })
    } catch (error) {
      lzNotifyErrorMessageFromResult(error)
    }
    setIsLoading(false)
  }, [dateRange, reportData])

  const exportToPdf = useCallback(async () => {
    if (reportData.length === 0) {
      lzNotifyErrorMessageFromResult('No data found to export')
      return
    }

    setIsLoading(true)
    try {
      Pdf(reportData, {
        reportTitle: 'TRAIL BALANCE V2',
        detailTitle: 'TRAIL BALANCE V2 DETAIL',
        asOf: dateRange[1]?.format('YYYY-MM-DD') || '',
        branchName: branchName || 'All Assigned Branches',
        companyName: companyName || 'WAH INDUSTRIES LIMITED',
      })
    } catch (error) {
      lzNotifyErrorMessageFromResult(error)
    }
    setIsLoading(false)
  }, [
    branchName,
    companyName,
    dateRange,
    reportData,
  ])

  return (
    <div
      className="container-fluid"
      style={{ padding: '1em', backgroundColor: 'white', minHeight: '100vh', fontFamily: 'Helvetica' }}
    >
      <Form layout="vertical" form={form} onFinish={getReportData} style={{ maxWidth: '100%', margin: '0 auto' }}>
        <Card
          title={
            <div style={{ position: 'relative', minHeight: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontSize: '22px', textAlign: 'center' }}>{companyName + ' Trail Balance V2'}</span>
              {lastLoadedAt ? (
                <span style={{ fontSize: '0.85rem', color: '#4b5563', position: 'absolute', right: 0 }}>
                  Last Loaded: {lastLoadedAt}
                </span>
              ) : null}
            </div>
          }
          style={{ width: '100%' }}
        >
          <Row gutter={[16, 16]}>
            <Col xs={15} sm={10} md={4}>
              <label>Date Range: *</label>
              <DatePicker.RangePicker
                onChange={(value) => changeValues('date_range', value)}
                style={{ width: '100%', height: 35 }}
                value={dateRange}
              />
            </Col>
            <Col xs={9} sm={6} md={3}>
              <label>Voucher Status:</label>
              <WidgetSelector
                selectedValue={voucherStatus}
                displayValue={voucherStatusName}
                options={VOUCHER_STATUS_DROPDOWN_OPTIONS}
                showCloudIcon={false}
                size="md"
                onChange={(values) => changeValues('voucher_status', values)}
              />
            </Col>
            <Col xs={12} sm={8} md={4}>
              <label>Branch:</label>
              <WidgetBranchSelector
                value={branchId}
                displayValue={branchName}
                branches={branches}
                placeholder="Select Branch"
                onChange={(values) => changeValues('branch_id', values)}
                showCloudIcon={false}
              />
            </Col>
            <Col xs={9} sm={6} md={3}>
              <label>Show Last Level Only:</label>
              <Switch
                style={{ marginTop: 10 }}
                value={showLastLevelOnly}
                onChange={(value) => changeValues('show_last_level_only', value)}
              />
            </Col>
            <Col xs={9} sm={6} md={3}>
              <label>Hide Zero Balances:</label>
              <Switch
                style={{ marginTop: 10 }}
                value={hideZeroBalances}
                onChange={(value) => changeValues('hide_zero_balances', value)}
              />
            </Col>
            <Col xs={9} sm={6} md={3}>
              <label>Hide Closing Entries:</label>
              <Switch
                style={{ marginTop: 10 }}
                value={hideClosingEntries}
                onChange={(value) => changeValues('hide_closing_entries', value)}
              />
            </Col>
            <Col xs={8} sm={4} md={4}>
              <Button style={{ marginTop: 35, marginRight: 10 }} type="primary" htmlType="submit" disabled={isLoading}>
                Search
              </Button>
              <Button
                style={{ marginTop: 35, color: '#fff', backgroundColor: '#E29C06' }}
                onClick={() => setSearchMore((value) => !value)}
                disabled={isLoading}
              >
                {searchMore ? 'Less...' : 'More...'}
              </Button>
            </Col>
          </Row>

          {searchMore && (
            <Row gutter={[16, 16]}>
              <Col xs={15} sm={10} md={5}>
                <label>Project:</label>
                <WidgetProjectSelector
                  onChange={(value) => changeValues('project_id', value)}
                  data={projects}
                  value={projectId}
                  displayValue={projectName}
                  height={35}
                  reloadDropdownData={loadProjects}
                />
              </Col>
            </Row>
          )}

          <br />
          <br />
          <div style={{ overflow: 'auto', margin: 'auto', width: '92%' }}>
            {base.hasRights('Export Pdf Trial Balance V2') && (
              <Button
                style={{ marginBottom: 10, float: 'right', backgroundColor: '#FF0000', color: '#fff' }}
                onClick={exportToPdf}
                disabled={isLoading}
                icon={<FilePdfOutlined />}
              />
            )}
            {base.hasRights('Export Trial Balance V2') && (
              <Button
                style={{ marginBottom: 10, float: 'right', backgroundColor: '#28A745', color: '#fff' }}
                onClick={exportToExcel}
                disabled={isLoading}
                icon={<FileExcelOutlined />}
              />
            )}
            <Table
              columns={columns}
              dataSource={reportData}
              pagination={false}
              expandable={{ expandedRowKeys: expandedRows, expandIcon }}
              bordered
              size="small"
              loading={{ indicator: <div><Spin /></div>, spinning: isLoading }}
            />
          </div>
        </Card>
      </Form>
    </div>
  )
}

export default TrailBalanceV2
